import { FloatingActivity, FloatingConfig, FloatingMessage, FloatingWhatsapp } from '../types'

/**
 * Avisos neutros que trae una landing nueva. Son mensajes genéricos y
 * editables: no se presenta ningún nombre inventado como cliente real.
 * Rotan automáticamente en el orden de la lista.
 */
export const DEFAULT_ACTIVITY_MESSAGES: FloatingMessage[] = [
  {
    title: 'Nueva solicitud recibida',
    text: 'Madrid',
    time: 'Hace unos minutos',
  },
  {
    title: 'Una empresa ha solicitado información',
    text: 'Tenerife',
    time: 'Hace 12 minutos',
  },
  { title: 'Nueva solicitud', text: 'Barcelona', time: 'Hace 24 minutos' },
  {
    title: 'Nueva reunión solicitada',
    text: 'Valencia',
    time: 'Hace 38 minutos',
  },
  {
    title: 'Una empresa está completando su solicitud',
    text: 'Málaga',
    time: 'Ahora mismo',
  },
  {
    title: 'Nueva solicitud recibida',
    text: 'Sevilla',
    time: 'Hace 50 minutos',
  },
  {
    title: 'Nueva solicitud recibida',
    text: 'Murcia',
    time: 'Hace 1 hora',
  },
]

/**
 * Listas por defecto anteriores. Una landing que guardó esos valores SIN
 * tocarlos recibe la lista nueva (ahora con Sevilla y Murcia); en cuanto el
 * usuario edita, añade o borra un mensaje deja de coincidir con cualquiera de
 * estas firmas y su versión personalizada se respeta siempre.
 */
const LEGACY_ACTIVITY_SIGNATURES = [
  // Primera versión: cinco avisos, con «Una empresa de Madrid».
  [
    'Nueva solicitud recibida|Una empresa de Madrid',
    'Una empresa ha solicitado información|Barcelona',
    'Nueva solicitud|Tenerife',
    'Nueva reunión solicitada|Valencia',
    'Una empresa está completando su solicitud|Málaga',
  ].join('//'),
  // Segunda versión: seis avisos, sin Murcia.
  [
    'Nueva solicitud recibida|Madrid',
    'Una empresa ha solicitado información|Tenerife',
    'Nueva solicitud|Barcelona',
    'Nueva reunión solicitada|Valencia',
    'Una empresa está completando su solicitud|Málaga',
    'Nueva solicitud recibida|Sevilla',
  ].join('//'),
]

function isLegacyDefault(messages: FloatingMessage[]): boolean {
  const signature = messages
    .map((m) => `${m.title ?? ''}|${m.text ?? ''}`)
    .join('//')
  return LEGACY_ACTIVITY_SIGNATURES.includes(signature)
}

/**
 * Configuración por defecto de los elementos flotantes.
 *
 * - WhatsApp flotante: DESACTIVADO. La burbuja de contacto durante la landing
 *   no debe aparecer sola; se activa a mano desde el constructor. (No tiene
 *   nada que ver con el botón de WhatsApp de la página de gracias, que sí
 *   viene activado por defecto en `data/thankYou.ts`.)
 * - Avisos de actividad: ACTIVADOS, con los mensajes neutros de arriba.
 */
export const DEFAULT_WHATSAPP: FloatingWhatsapp = {
  enabled: false,
  phone: '',
  message: 'Hola, quiero información sobre Sistema AVA.',
  label: '',
  showCard: false,
  cardTitle: '¿Tienes alguna duda?',
  cardText: 'Escríbenos por WhatsApp y te respondemos enseguida.',
  position: 'derecha',
  size: 48,
  devices: ['desktop', 'tablet', 'mobile'],
}

export const DEFAULT_ACTIVITY: FloatingActivity = {
  enabled: true,
  mode: 'manual',
  messages: DEFAULT_ACTIVITY_MESSAGES,
  position: 'izquierda',
  delay: 8,
  duration: 6,
  interval: 22,
  max: DEFAULT_ACTIVITY_MESSAGES.length,
  showTime: true,
  devices: ['desktop', 'tablet', 'mobile'],
}

export const DEFAULT_FLOATING: FloatingConfig = {
  whatsapp: DEFAULT_WHATSAPP,
  activity: DEFAULT_ACTIVITY,
}

/**
 * Completa SOLO las claves ausentes.
 *
 * Se usa `??`, nunca `||`: `false` es un valor legítimo y debe sobrevivir, de
 * modo que si el usuario desactiva un elemento y guarda, el valor guardado
 * gana sobre el valor por defecto y no vuelve a activarse solo.
 */
export function withFloatingDefaults(value?: unknown): FloatingConfig {
  const config = (value ?? {}) as FloatingConfig
  const whatsapp = config.whatsapp
  const activity = config.activity
  return {
    whatsapp: whatsapp
      ? { ...DEFAULT_WHATSAPP, ...whatsapp, enabled: whatsapp.enabled ?? false }
      : { ...DEFAULT_WHATSAPP },
    activity: activity
      ? {
          ...DEFAULT_ACTIVITY,
          ...activity,
          enabled: activity.enabled ?? true,
          messages:
            activity.messages && activity.messages.length > 0
              ? isLegacyDefault(activity.messages)
                ? DEFAULT_ACTIVITY_MESSAGES
                : activity.messages
              : DEFAULT_ACTIVITY_MESSAGES,
        }
      : { ...DEFAULT_ACTIVITY },
  }
}
